// 設備表格的額外欄位（DEV-01）：雲錄席次（帳單主體的 seats）、AI（家戶的 AI 運算指派）、位置（org 場域名稱）。
// 各自快取、拿不到（沒權限 / 服務沒開）就沒有，不擋表格。
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { aiApi, billingApi, hasOrgPerm, orgApi, type BillingSubjectRef } from '../../lib/planeApi';

export interface DeviceExtras {
  /** `${home}/${device_ref}` → 席次模式 */
  seat: Map<string, 'event' | 'continuous'>;
  /** `${home}/${device_ref}` → AI 提供者名稱 */
  ai: Map<string, string>;
  /** home_id → 場域名稱（企業家戶） */
  site: Map<string, string>;
}

export function useDeviceExtras(): DeviceExtras {
  const { orgs: allOrgs, homes, visible } = useScope();
  // 組織的安全政策這次登入不符（要求 MFA…）就不問那個組織；只經分享進來的家戶沒有 AI 指派
  const orgs = useMemo(() => allOrgs.filter((o) => !o.notice), [allOrgs]);
  const memberVisible = useMemo(() => visible.filter((h) => !h.external), [visible]);
  const subjects = useMemo<BillingSubjectRef[]>(() => {
    const out: BillingSubjectRef[] = orgs
      .filter((o) => hasOrgPerm(o, 'org.billing') || hasOrgPerm(o, 'org.audit'))
      .map((o) => ({ api: o.api, kind: 'org', id: o.org_id }));
    for (const h of homes)
      if (h.org_kind === 'personal' && (h.role === 'owner' || h.role === 'admin')) out.push({ api: h.endpoints.api, kind: 'home', id: h.home_id });
    return out;
  }, [orgs, homes]);
  const bills = useQueries({
    queries: subjects.map((s) => ({ queryKey: ['billing', s.kind, s.id], queryFn: () => billingApi.overview(s), staleTime: 60_000, retry: false })),
  });
  const ais = useQueries({
    queries: memberVisible.map((h) => ({
      queryKey: ['ai-providers', h.home_id],
      queryFn: () => aiApi.providers(h.endpoints.api, h.home_id),
      staleTime: 60_000,
      retry: false,
    })),
  });
  const sites = useQueries({
    queries: orgs.map((o) => ({ queryKey: ['sites', o.org_id], queryFn: () => orgApi.sites(o), staleTime: 60_000, retry: false })),
  });
  const billKey = bills.map((q) => q.dataUpdatedAt).join();
  const aiKey = ais.map((q) => q.dataUpdatedAt).join();
  const siteKey = sites.map((q) => q.dataUpdatedAt).join();
  return useMemo(() => {
    const seat = new Map<string, 'event' | 'continuous'>();
    for (const q of bills)
      for (const s of q.data?.seats ?? [])
        if (s.assigned_ref && s.assigned_home_id && (s.status === 'active' || s.status === 'grace'))
          seat.set(`${s.assigned_home_id}/${s.assigned_ref}`, s.mode);
    const ai = new Map<string, string>();
    ais.forEach((q, i) => {
      for (const p of q.data ?? []) for (const a of p.assignments) ai.set(`${memberVisible[i]?.home_id}/${a.device_ref}`, p.name || p.device_ref);
    });
    const siteName = new Map<string, string>();
    for (const q of sites) for (const s of q.data ?? []) siteName.set(s.site_id, s.name);
    const site = new Map<string, string>();
    for (const h of homes) if (h.site_id && siteName.has(h.site_id)) site.set(h.home_id, siteName.get(h.site_id)!);
    return { seat, ai, site };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billKey, aiKey, siteKey, homes, memberVisible]);
}
